'use client'
import React, { useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import { faExclamationCircle, faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { fetchCinematics } from '@/services/fetch-cinematics'
import { useCinematicsStore } from '@/cinematics/stores/CinematicsStore'
import { useTranslation } from '@/localization/useTranslation'

import '@/styles/system-pages/system.module.scss'
import '@/styles/system-pages/error.scss'

const MAX_ATTEMPTS = 3
const backoffMs = (attempt: number) => 1000 * 2 ** attempt // 1s, 2s, 4s

interface ICinematicsRecoveryProps {
  tag: string
  initialLanguage: string
  children: React.ReactNode
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Guards against a swallowed SSR cinematic fetch failure: when the store was
 * never initialized (originalCinematics stays null), retries fetchCinematics on
 * the client with bounded exponential backoff instead of leaving a blank shell.
 * On success the store is initialized and children render; on exhaustion a
 * minimal error state with reload is shown and the failure is reported to Sentry.
 */
export const CinematicsRecovery: React.FC<ICinematicsRecoveryProps> = ({ tag, initialLanguage, children }) => {
  const originalCinematics = useCinematicsStore((state) => state.originalCinematics)
  const initialize = useCinematicsStore((state) => state.initialize)
  const [failed, setFailed] = useState(false)
  const attemptStarted = useRef(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (originalCinematics !== null || attemptStarted.current) return
    attemptStarted.current = true

    let cancelled = false

    const recover = async () => {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          const data = await fetchCinematics({ tag })
          if (cancelled) return
          if (data && Object.keys(data).length > 0) {
            initialize(data, initialLanguage, false)
            return
          }
        } catch {
          if (cancelled) return
        }
        await sleep(backoffMs(attempt))
        if (cancelled) return
      }
      Sentry.captureException(new Error(`Cinematics recovery failed after ${MAX_ATTEMPTS} attempts (tag: ${tag})`))
      setFailed(true)
    }

    void recover()

    return () => {
      cancelled = true
    }
  }, [originalCinematics, tag, initialLanguage, initialize])

  if (originalCinematics !== null) return <>{children}</>

  if (failed) {
    return (
      <section className="error-container">
        <div className="error-content">
          <h1 className="error-title">
            <FontAwesomeIcon icon={faExclamationCircle} />
          </h1>
          <h2 className="error-subtitle">{t('Something went wrong loading the app')}</h2>
          <div className="error-link" onClick={() => window.location.reload()}>
            <FontAwesomeIcon icon={faRotateRight} />
            <span>{t('Reload')}</span>
          </div>
        </div>
      </section>
    )
  }

  return <LoadingOverlay isLoading backgroundIntensity="solid" />
}
